import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import {
  AlertTriangle,
  Calendar,
  Clock,
  Bell,
  Plus,
  Check,
  CheckCircle,
  MapPin,
  Phone,
  User,
  Users,
  Mail,
  X,
  XCircle,
  Edit3,
  AlertCircle,
  Minus,
  ChevronDown,
  FileText,
  AlarmClock
} from 'lucide-react';
import { reminderApi, Reminder } from '../../services/reminderService';
import { appointmentsApi, Appointment, EntityType } from '../../services/appointmentsService';
import { handleApiError } from '../../utils/errorHandler';
import { useAuth } from '../../contexts/AuthContext';
import AddAppointmentModal from '../schedule/AddAppointmentModal';
import AddReminderModal from '../schedule/AddReminderModal';
import ReassignControl from '../common/ReassignControl';

interface ClientScheduleTabProps {
  clientId: number;
  clientName: string;
  clientEmail?: string;
}

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const Container = styled.div`
  padding: 20px;

  @media (max-width: 767px) {
    padding: 16px;
  }
`;

/* ========== Header ========== */
const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid var(--border-color);

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const PageTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TitleIcon = styled.span`
  color: var(--primary-color);
  display: flex;
  align-items: center;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 767px) {
    width: 100%;
    flex-direction: column;
  }
`;

const PrimaryHeaderButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  padding: 10px 20px;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--primary-color);
    color: white;
  }

  @media (max-width: 767px) {
    justify-content: center;
  }
`;

const SecondaryHeaderButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  padding: 10px 20px;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--background-secondary);
    border-color: var(--text-secondary);
    color: var(--text-primary);
  }

  @media (max-width: 767px) {
    justify-content: center;
  }
`;

/* ========== Section Structure ========== */
const Section = styled.div`
  margin-bottom: 40px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 20px;
  padding-bottom: 14px;
  border-bottom: 2px solid var(--border-color);
`;

const SectionIconWrapper = styled.div<{ $color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$color || 'var(--primary-color)'};
  flex-shrink: 0;
`;

const SectionTitleGroup = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex: 1;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
`;

const SectionCount = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: var(--text-secondary);
`;

/* ========== Items List ========== */
const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ItemRow = styled.div`
  padding: 18px 20px;
  background: var(--surface-color);
  border-radius: var(--border-radius-md);
  border: 1px solid var(--border-color);
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 6px;

  @media (max-width: 767px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const ItemTitle = styled.div`
  font-size: 17px;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.4;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ItemDateTime = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
  white-space: nowrap;
  flex-shrink: 0;
  background: var(--background-secondary);
  padding: 4px 12px;
  border-radius: var(--border-radius-sm);
`;

const ItemDescription = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: 12px;
  margin-top: 8px;
`;

const ItemMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
  margin-top: 8px;
  margin-bottom: 12px;
  font-size: 13px;
  color: var(--text-tertiary, #9ca3af);
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    opacity: 0.7;
  }
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const StatusIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--text-secondary);

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    min-width: 16px;
    min-height: 16px;
  }
`;

const ItemFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--border-color);

  @media (max-width: 767px) {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
`;

const ItemActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const PrimaryActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 18px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: #16a34a;
  color: white;
  transition: all 0.2s ease;

  &:hover {
    background: #15803d;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SecondaryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  background: transparent;
  color: var(--text-secondary);
  transition: all 0.2s ease;

  &:hover {
    background: var(--background-secondary);
    border-color: var(--text-secondary);
  }
`;

const DangerButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border: 1px solid transparent;
  border-radius: var(--border-radius-sm);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  background: transparent;
  color: var(--text-tertiary, #9ca3af);
  transition: all 0.2s ease;

  &:hover {
    background: rgba(220, 38, 38, 0.08);
    color: #dc2626;
    border-color: rgba(220, 38, 38, 0.2);
  }
`;

/* ========== Empty State ========== */
const EmptyState = styled.div`
  padding: 32px 20px;
  text-align: center;
  color: var(--text-tertiary, #9ca3af);
  font-size: 14px;
  background: var(--background-secondary);
  border-radius: var(--border-radius-md);
`;

/* ========== Loading ========== */
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
  color: var(--text-secondary);
  font-size: 14px;
`;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Appointment Status: 0=Scheduled, 1=InProgress, 2=Completed, 3=Cancelled, 4=Rescheduled
const getAppointmentStatusIcon = (status: number) => {
  switch (status) {
    case 0: return <Calendar size={16} strokeWidth={2} style={{ color: '#008EA2' }} />;
    case 1: return <Clock size={16} strokeWidth={2} style={{ color: '#d97706' }} />;
    case 2: return <CheckCircle size={16} strokeWidth={2} style={{ color: '#16a34a' }} />;
    case 3: return <XCircle size={16} strokeWidth={2} style={{ color: '#dc2626' }} />;
    case 4: return <Calendar size={16} strokeWidth={2} style={{ color: '#d97706' }} />;
    default: return <Calendar size={16} strokeWidth={2} style={{ color: '#008EA2' }} />;
  }
};

// Confirmation Status: 1=Pending, 2=Confirmed, 3=Declined
const getConfirmationIcon = (status: number) => {
  switch (status) {
    case 2: return <Check size={16} strokeWidth={2} style={{ color: '#16a34a' }} />;
    case 3: return <X size={16} strokeWidth={2} style={{ color: '#dc2626' }} />;
    default: return <Clock size={16} strokeWidth={2} style={{ color: '#d97706' }} />;
  }
};

// Priority icons (gray) - 1=High, 2=Medium, 3=Low
const getPriorityIcon = (priority: number) => {
  switch (priority) {
    case 1: return <AlertCircle size={14} strokeWidth={2} />;
    case 2: return <Minus size={14} strokeWidth={2} />;
    case 3: return <ChevronDown size={14} strokeWidth={2} />;
    default: return <Minus size={14} strokeWidth={2} />;
  }
};

// Reminder type icon (gray)
const getReminderTypeIcon = () => {
  return <FileText size={14} strokeWidth={2} />;
};

// Appointment type icon (gray)
const getAppointmentTypeIcon = () => {
  return <Users size={14} strokeWidth={2} />;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ClientScheduleTab: React.FC<ClientScheduleTabProps> = ({ clientId, clientName, clientEmail }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [urgentReminders, setUrgentReminders] = useState<Reminder[]>([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState<Appointment[]>([]);
  const [futureReminders, setFutureReminders] = useState<Reminder[]>([]);

  const [showAddAppointmentModal, setShowAddAppointmentModal] = useState(false);
  const [showAddReminderModal, setShowAddReminderModal] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const [reminders, appointments] = await Promise.all([
        reminderApi.getClientReminders(clientId),
        appointmentsApi.getClientAppointments(clientId)
      ]);

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const urgent = reminders.filter(r => {
        const dueDate = new Date(r.dueDateTime);
        return r.status === 1 && dueDate < tomorrow;
      }).sort((a, b) => a.priority - b.priority);

      const future = reminders.filter(r => {
        const dueDate = new Date(r.dueDateTime);
        return r.status === 1 && dueDate >= tomorrow;
      }).sort((a, b) => new Date(a.dueDateTime).getTime() - new Date(b.dueDateTime).getTime());

      const upcoming = appointments
        .filter(a => a.status !== 3 && a.status !== 4)
        .sort((a, b) => {
          if (!a.appointmentDateTime) return 1;
          if (!b.appointmentDateTime) return -1;
          return new Date(a.appointmentDateTime).getTime() - new Date(b.appointmentDateTime).getTime();
        });

      setUrgentReminders(urgent);
      setUpcomingAppointments(upcoming);
      setFutureReminders(future);
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בטעינת נתוני התיאום'));
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCompleteReminder = async (reminderId: number) => {
    try {
      await reminderApi.handleReminder(reminderId, { handledNotes: 'הושלם' });
      loadData();
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בסימון התזכורת כהושלמה'));
    }
  };

  const handleCompleteAppointment = async (appointmentId: number) => {
    try {
      await appointmentsApi.completeAppointment(appointmentId, { completionNotes: 'הפגישה הושלמה' });
      loadData();
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בסימון הפגישה כהושלמה'));
    }
  };

  const handleReassignAppointment = async (appointmentId: number, userId: number) => {
    try {
      await appointmentsApi.reassign(appointmentId, userId);
      toast.success('המשויך עודכן בהצלחה');
      loadData();
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בעדכון המשויך'));
      throw error;
    }
  };

  const handleReassignReminder = async (reminderId: number, userId: number) => {
    try {
      await reminderApi.reassign(reminderId, userId);
      toast.success('המשויך עודכן בהצלחה');
      loadData();
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בעדכון המשויך'));
      throw error;
    }
  };

  const handleSendInvitation = async (appointmentId: number) => {
    if (!clientEmail || clientEmail.trim() === '') {
      toast.error('לא ניתן לשלוח מייל - ללקוח אין כתובת דוא"ל מוגדרת במערכת');
      return;
    }

    try {
      await appointmentsApi.sendInvitation(appointmentId);
      toast.success('מייל אישור נשלח ללקוח בהצלחה');
      loadData();
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בשליחת מייל אישור'));
    }
  };

  const handleManualConfirm = async (appointmentId: number) => {
    if (!window.confirm('האם אתה בטוח שברצונך לאשר את הפגישה ידנית?')) {
      return;
    }
    try {
      await appointmentsApi.manualConfirmAppointment(appointmentId);
      toast.success('הפגישה אושרה בהצלחה');
      loadData();
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה באישור הפגישה'));
    }
  };

  const handleManualCancel = async (appointmentId: number) => {
    if (!window.confirm('האם אתה בטוח שברצונך לבטל את הפגישה?')) {
      return;
    }
    try {
      await appointmentsApi.manualCancelAppointment(appointmentId);
      toast.success('הפגישה בוטלה בהצלחה');
      loadData();
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בביטול הפגישה'));
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingContainer>טוען נתוני תיאום ומשימות...</LoadingContainer>
      </Container>
    );
  }

  return (
    <Container>
      {/* Header */}
      <PageHeader>
        <PageTitle>
          <TitleIcon><Calendar size={22} strokeWidth={2} /></TitleIcon>
          תיאום ומשימות
        </PageTitle>
        <HeaderActions>
          <PrimaryHeaderButton onClick={() => setShowAddAppointmentModal(true)}>
            <Plus size={16} strokeWidth={2} />
            הוסף פגישה
          </PrimaryHeaderButton>
          <SecondaryHeaderButton onClick={() => setShowAddReminderModal(true)}>
            <Bell size={16} strokeWidth={2} />
            הוסף תזכורת
          </SecondaryHeaderButton>
        </HeaderActions>
      </PageHeader>

      {/* Urgent Reminders */}
      <Section>
        <SectionHeader>
          <SectionIconWrapper $color="#dc2626">
            <AlertTriangle size={22} strokeWidth={2} />
          </SectionIconWrapper>
          <SectionTitleGroup>
            <SectionTitle>תזכורות דחופות</SectionTitle>
            {urgentReminders.length > 0 && (
              <SectionCount>({urgentReminders.length})</SectionCount>
            )}
          </SectionTitleGroup>
        </SectionHeader>

        <ItemsList>
          {urgentReminders.length === 0 ? (
            <EmptyState>אין תזכורות דחופות להיום</EmptyState>
          ) : (
            urgentReminders.map(reminder => (
              <ItemRow key={reminder.id}>
                <ItemHeader>
                  <ItemTitle>{reminder.title}</ItemTitle>
                  <ItemDateTime>
                    <Clock size={14} strokeWidth={2} />
                    {formatDate(reminder.dueDateTime)}
                  </ItemDateTime>
                </ItemHeader>

                {reminder.description && (
                  <ItemDescription>{reminder.description}</ItemDescription>
                )}

                <ItemFooter>
                  <StatusRow>
                    <StatusIndicator>
                      {getPriorityIcon(reminder.priority)}
                      {reminder.priorityName}
                    </StatusIndicator>
                    <StatusIndicator>
                      {getReminderTypeIcon()}
                      {reminder.reminderTypeName}
                    </StatusIndicator>
                    {reminder.isOverdue && (
                      <StatusIndicator>
                        <AlarmClock size={14} strokeWidth={2} />
                        פג תוקף
                      </StatusIndicator>
                    )}
                    <ReassignControl
                      assignedUserId={reminder.userId}
                      assignedUserName={reminder.userName}
                      onReassign={(userId) => handleReassignReminder(reminder.id, userId)}
                    />
                  </StatusRow>

                  <ItemActions>
                    <PrimaryActionButton onClick={() => handleCompleteReminder(reminder.id)}>
                      <Check size={16} strokeWidth={2} />
                      סמן כבוצע
                    </PrimaryActionButton>
                  </ItemActions>
                </ItemFooter>
              </ItemRow>
            ))
          )}
        </ItemsList>
      </Section>

      {/* Upcoming Appointments */}
      <Section>
        <SectionHeader>
          <SectionIconWrapper $color="var(--primary-color)">
            <Calendar size={22} strokeWidth={2} />
          </SectionIconWrapper>
          <SectionTitleGroup>
            <SectionTitle>פגישות קרובות</SectionTitle>
            {upcomingAppointments.length > 0 && (
              <SectionCount>({upcomingAppointments.length})</SectionCount>
            )}
          </SectionTitleGroup>
        </SectionHeader>

        <ItemsList>
          {upcomingAppointments.length === 0 ? (
            <EmptyState>אין פגישות מתוזמנות</EmptyState>
          ) : (
            upcomingAppointments.map(appointment => (
              <ItemRow key={appointment.id}>
                <ItemHeader>
                  <ItemTitle>
                    <User size={18} strokeWidth={2} />
                    {appointment.doctorName}
                  </ItemTitle>
                  {appointment.appointmentDateTime && (
                    <ItemDateTime>
                      <Clock size={14} strokeWidth={2} />
                      {formatDate(appointment.appointmentDateTime)}
                    </ItemDateTime>
                  )}
                </ItemHeader>

                {(appointment.address || appointment.phoneForScheduling) && (
                  <ItemMeta>
                    {appointment.address && (
                      <MetaItem>
                        <MapPin size={14} strokeWidth={2} />
                        {appointment.address}
                      </MetaItem>
                    )}
                    {appointment.phoneForScheduling && (
                      <MetaItem>
                        <Phone size={14} strokeWidth={2} />
                        {appointment.phoneForScheduling}
                      </MetaItem>
                    )}
                  </ItemMeta>
                )}

                {appointment.instructionsForClient && (
                  <ItemDescription>{appointment.instructionsForClient}</ItemDescription>
                )}

                <ItemFooter>
                  <StatusRow>
                    <StatusIndicator>
                      {getAppointmentStatusIcon(appointment.status)}
                      {appointment.statusName}
                    </StatusIndicator>
                    <StatusIndicator>
                      {getAppointmentTypeIcon()}
                      {appointment.appointmentTypeName}
                    </StatusIndicator>
                    {appointment.clientConfirmationStatusName && (
                      <StatusIndicator>
                        {getConfirmationIcon(appointment.clientConfirmationStatus)}
                        {appointment.clientConfirmationStatusName}
                      </StatusIndicator>
                    )}
                    <ReassignControl
                      assignedUserId={appointment.assignedUserId}
                      assignedUserName={appointment.assignedUserName}
                      onReassign={(userId) => handleReassignAppointment(appointment.id, userId)}
                    />
                  </StatusRow>

                  <ItemActions>
                    {!appointment.emailInvitationSent && appointment.appointmentDateTime && (
                      <SecondaryButton onClick={() => handleSendInvitation(appointment.id)}>
                        <Mail size={14} strokeWidth={2} />
                        שלח מייל
                      </SecondaryButton>
                    )}

                    {appointment.clientConfirmationStatus === 1 && (
                      <SecondaryButton onClick={() => handleManualConfirm(appointment.id)}>
                        <Edit3 size={14} strokeWidth={2} />
                        אשר ידנית
                      </SecondaryButton>
                    )}

                    {appointment.clientConfirmationStatus !== 3 && appointment.status !== 4 && (
                      <DangerButton onClick={() => handleManualCancel(appointment.id)}>
                        <X size={14} strokeWidth={2} />
                        בטל
                      </DangerButton>
                    )}

                    {appointment.status !== 2 && (
                      <PrimaryActionButton onClick={() => handleCompleteAppointment(appointment.id)}>
                        <Check size={16} strokeWidth={2} />
                        הושלם
                      </PrimaryActionButton>
                    )}
                  </ItemActions>
                </ItemFooter>
              </ItemRow>
            ))
          )}
        </ItemsList>
      </Section>

      {/* Future Reminders */}
      <Section>
        <SectionHeader>
          <SectionIconWrapper $color="var(--text-secondary)">
            <Bell size={22} strokeWidth={2} />
          </SectionIconWrapper>
          <SectionTitleGroup>
            <SectionTitle>תזכורות עתידיות</SectionTitle>
            {futureReminders.length > 0 && (
              <SectionCount>({futureReminders.length})</SectionCount>
            )}
          </SectionTitleGroup>
        </SectionHeader>

        <ItemsList>
          {futureReminders.length === 0 ? (
            <EmptyState>אין תזכורות עתידיות</EmptyState>
          ) : (
            futureReminders.map(reminder => (
              <ItemRow key={reminder.id}>
                <ItemHeader>
                  <ItemTitle>{reminder.title}</ItemTitle>
                  <ItemDateTime>
                    <Clock size={14} strokeWidth={2} />
                    {formatDate(reminder.dueDateTime)}
                  </ItemDateTime>
                </ItemHeader>

                {reminder.description && (
                  <ItemDescription>{reminder.description}</ItemDescription>
                )}

                <ItemFooter>
                  <StatusRow>
                    <StatusIndicator>
                      {getPriorityIcon(reminder.priority)}
                      {reminder.priorityName}
                    </StatusIndicator>
                    <StatusIndicator>
                      {getReminderTypeIcon()}
                      {reminder.reminderTypeName}
                    </StatusIndicator>
                    <ReassignControl
                      assignedUserId={reminder.userId}
                      assignedUserName={reminder.userName}
                      onReassign={(userId) => handleReassignReminder(reminder.id, userId)}
                    />
                  </StatusRow>

                  <ItemActions>
                    <PrimaryActionButton onClick={() => handleCompleteReminder(reminder.id)}>
                      <Check size={16} strokeWidth={2} />
                      סמן כבוצע
                    </PrimaryActionButton>
                  </ItemActions>
                </ItemFooter>
              </ItemRow>
            ))
          )}
        </ItemsList>
      </Section>

      {/* Modals */}
      {showAddAppointmentModal && (
        <AddAppointmentModal
          isOpen={showAddAppointmentModal}
          onClose={() => setShowAddAppointmentModal(false)}
          onSuccess={() => {
            setShowAddAppointmentModal(false);
            loadData();
          }}
          defaultAssignedUserId={user?.id || 0}
          entityType={EntityType.Client}
          entityId={clientId}
          entityName={clientName}
        />
      )}

      {showAddReminderModal && (
        <AddReminderModal
          isOpen={showAddReminderModal}
          onClose={() => setShowAddReminderModal(false)}
          onSuccess={() => {
            setShowAddReminderModal(false);
            loadData();
          }}
          defaultUserId={user?.id || 0}
          preSelectedClientId={clientId}
        />
      )}
    </Container>
  );
};

export default ClientScheduleTab;
